import React, { useState, useEffect } from 'react';
import {
	CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
	CButton, CFormLabel, CFormInput, CFormSelect,
} from '@coreui/react';
import { toast } from 'react-toastify';
import AxiosInstance from 'src/components/Axios';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import {
	invalidateCachedRequest, invalidateLettersList,
	invalidateCachedSession, invalidateCachedSupportHistory,
} from 'src/utils/supportRequestCache';

const urlApiSessions = '/support-sessions';
const urlApiRequests = '/support-requests';

const SupportChangeSessionModal = ({ open, onClose, requestId, currentSessionId, userEmail, onSuccess }) => {
	const [mode, setMode] = useState('existing');
	const [newSessionSubject, setNewSessionSubject] = useState('');
	const [userSessions, setUserSessions] = useState([]);
	const [selectedExistingSession, setSelectedExistingSession] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!open) return;
		setMode('existing');
		setNewSessionSubject('');
		setSelectedExistingSession('');
		setUserSessions([]);
		if (userEmail) {
			AxiosInstance.get(urlApiSessions, { params: { user_email: userEmail } })
				.then(res => setUserSessions(Array.isArray(res.data) ? res.data : (res.data.data ?? [])))
				.catch(() => { });
		}
	}, [open, userEmail]);

	const handleSubmit = async () => {
		setLoading(true);
		try {
			let payload;
			if (mode === 'new') {
				if (!newSessionSubject.trim()) {
					toast.warning('Вкажіть тему нової сесії');
					setLoading(false);
					return;
				}
				payload = { new_session_subject: newSessionSubject.trim() };
			} else {
				if (!selectedExistingSession) {
					toast.warning('Оберіть сесію');
					setLoading(false);
					return;
				}
				payload = { session_id: parseInt(selectedExistingSession) };
			}
			const oldSessionId = currentSessionId;
			const res = await AxiosInstance.put(`${urlApiRequests}/${requestId}/session`, payload);
			const newSessionId = res.data?.session_id;
			const movedToSubject = res.data?.subject ?? null;
			invalidateCachedRequest(requestId);
			invalidateLettersList();
			if (oldSessionId) invalidateCachedSession(oldSessionId);
			if (newSessionId) invalidateCachedSession(newSessionId);
			if (userEmail) invalidateCachedSupportHistory(userEmail);
			toast.success('Сесію змінено');
			onClose();
			onSuccess({ newSessionId, oldSessionId, newSessionSubject: movedToSubject });
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка зміни сесії');
		} finally {
			setLoading(false);
		}
	};

	return (
		<CModal visible={open} onClose={() => !loading && onClose()}>
			<CModalHeader>
				<CModalTitle>Змінити сесію для листа #{requestId}</CModalTitle>
			</CModalHeader>
			<CModalBody>
				<FormSubmitOverlay submitting={loading}>
					<div className="d-flex gap-2 mb-3">
						<CButton
							color={mode === 'existing' ? 'primary' : 'secondary'}
							variant={mode === 'existing' ? undefined : 'outline'}
							onClick={() => setMode('existing')}
						>
							Існуюча сесія
						</CButton>
						<CButton
							color={mode === 'new' ? 'primary' : 'secondary'}
							variant={mode === 'new' ? undefined : 'outline'}
							onClick={() => setMode('new')}
						>
							Нова сесія
						</CButton>
					</div>
					{mode === 'new' ? (
						<>
							<CFormLabel>Тема нової сесії *</CFormLabel>
							<CFormInput
								value={newSessionSubject}
								onChange={e => setNewSessionSubject(e.target.value)}
								placeholder="Введіть тему нової сесії"
								autoComplete="off"
							/>
						</>
					) : (
						<>
							<CFormLabel>Оберіть сесію</CFormLabel>
							<CFormSelect
								value={selectedExistingSession}
								onChange={e => setSelectedExistingSession(e.target.value)}
							>
								<option value="">— оберіть сесію —</option>
								{userSessions.filter(s => s.id !== currentSessionId).map(s => (
									<option key={s.id} value={s.id}>
										#{s.id} {s.subject} ({s.status})
									</option>
								))}
							</CFormSelect>
						</>
					)}
				</FormSubmitOverlay>
			</CModalBody>
			<CModalFooter>
				<CButton
					color="primary"
					onClick={handleSubmit}
					disabled={loading}
					title={mode === 'new' ? 'Створити нову сесію і перемістити лист' : 'Перемістити лист до обраної сесії'}
				>
					{loading ? 'Переміщення...' : (mode === 'new' ? 'Створити і перемістити' : 'Перемістити')}
				</CButton>
				<CButton
					color="secondary"
					variant="outline"
					onClick={onClose}
					disabled={loading}
					title="Скасувати"
				>
					Скасувати
				</CButton>
			</CModalFooter>
		</CModal>
	);
};

export default SupportChangeSessionModal;
